<script setup lang="ts">
import { ArrowLeft, X, Users, Maximize2, Calendar, ArrowUpRight, CornerDownLeft } from 'lucide-vue-next';

interface InsightResult {
  type: 'users' | 'user' | 'calendar';
  title: string;
  meta: string;
  showExpand?: boolean;
}

defineProps<{
  expanded?: boolean;
  displayedText: string;
  results: InsightResult[];
}>();

defineEmits<{
  (e: 'update:activeStep', step: number): void;
}>();
</script>

<template>
  <div class="insight-modal-overlay-prod dark">
    <!-- HEADER: Hidden on Expanded -->
    <div v-if="!expanded" class="insight-modal-header-prod dark">
      <div class="header-left">
         <ArrowLeft :size="20" class="back-icon hide-mobile" />
         <div class="insight-search-mock-prod dark">
           <span>{{ displayedText }}</span>
           <span class="cursor active">|</span>
         </div>
      </div>
      <div class="header-right">
        <X :size="20" class="clear-icon" />
        <div class="confirm-btn-circle">
          <CornerDownLeft :size="18" />
        </div>
      </div>
    </div>

    <!-- MAIN AREA: Changes based on Expanded State -->
    <div v-if="expanded" class="insight-main-container dark expanded">
      <div class="expanded-title-area">
         <div class="sidebar-back-pill dark hide-mobile"><ArrowLeft :size="18" /><span>Back</span></div>
         <h2 class="main-entity-title">Arsenal</h2>
         <div class="sub-header-line">
            <span class="sub-title">Arsenal</span>
            <div class="divider"></div>
         </div>
      </div>

      <div class="suggestion-list-mock dark">
        <div class="suggestion-item dark">
          <div class="item-icon dark"><Calendar :size="20" /></div>
          <div class="item-text dark">
            <span class="title">West Ham - <strong>Arsenal</strong></span>
            <span class="meta">Sun 16:30</span>
          </div>
          <div class="item-arrow dark"><ArrowUpRight :size="18" /></div>
        </div>
        <div class="suggestion-item dark">
          <div class="item-icon dark"><Calendar :size="20" /></div>
          <div class="item-text dark">
            <span class="title"><strong>Arsenal</strong> - Burnley</span>
            <span class="meta">Mon 18th 20:00</span>
          </div>
          <div class="item-arrow dark"><ArrowUpRight :size="18" /></div>
        </div>
        <div class="suggestion-item dark">
          <div class="item-icon dark"><Calendar :size="20" /></div>
          <div class="item-text dark">
            <span class="title">PSG - <strong>Arsenal</strong></span>
            <span class="meta">Sat 30th 17:00</span>
          </div>
          <div class="item-arrow dark"><ArrowUpRight :size="18" /></div>
        </div>
      </div>
    </div>

    <!-- STANDARD VIEW -->
    <div v-else class="insight-main-container dark">
      <transition name="fade" mode="out-in">
        <div class="suggestion-list-mock dark" :key="displayedText">
          <div v-for="(res, idx) in results" :key="idx" 
               class="suggestion-item dark" 
               :class="{ 'entity-row': res.showExpand }">
            <div class="item-icon dark">
              <Users v-if="res.type === 'users'" :size="20" />
              <Users v-else-if="res.type === 'user'" :size="18" class="text-secondary" />
              <Calendar v-else-if="res.type === 'calendar'" :size="20" />
            </div>
            <div class="item-text dark">
              <span class="title">
                <template v-if="displayedText && res.title.toLowerCase().startsWith(displayedText.toLowerCase())">
                   <strong>{{ res.title.slice(0, displayedText.length) }}</strong>{{ res.title.slice(displayedText.length) }}
                </template>
                <template v-else>{{ res.title }}</template>
              </span>
              <span v-if="res.meta" class="meta">{{ res.meta }}</span>
            </div>
            
            <div v-if="res.showExpand" class="item-actions dark">
              <Maximize2 :size="18" class="step-trigger" @click="$emit('update:activeStep', 2)" />
            </div>
            <div v-else class="action-pair">
               <ArrowUpRight :size="18" />
               <CornerDownLeft :size="18" />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(5px); }

.insight-modal-overlay-prod.dark { position: absolute; inset: 0; background: #111113; z-index: 50; display: flex; flex-direction: column; }
.insight-modal-header-prod.dark { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: #111113; border-bottom: 1px solid rgba(255,255,255,0.05); }
.header-left { display: flex; align-items: center; gap: 20px; }
.back-icon { color: #fff; }
.insight-search-mock-prod.dark { font-weight: 600; font-size: 1.1rem; color: #fff; }
.header-right { display: flex; align-items: center; gap: 16px; }
.clear-icon { color: #71717a; }
.confirm-btn-circle { width: 44px; height: 44px; background: #e21b0d; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; box-shadow: 0 4px 12px rgba(226, 27, 13, 0.4); }
.cursor.active { color: #fff; margin-left: 2px; }

.insight-main-container { flex-grow: 1; padding: 12px 0; overflow-y: auto; }
.expanded-title-area { padding: 20px 24px 0; display: flex; flex-direction: column; gap: 16px; }
.sidebar-back-pill { display: inline-flex; align-items: center; gap: 12px; padding: 6px 14px; background: #1c1c1f; border-radius: 100px; border: 1px solid #3f3f46; color: #fff; font-weight: 700; font-size: 0.85rem; width: fit-content; }
.main-entity-title { font-size: 1.8rem; font-weight: 900; color: #fff; margin: 0; }
.sub-header-line { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.sub-header-line .sub-title { font-size: 1.2rem; font-weight: 800; color: #fff; }
.sub-header-line .divider { height: 1px; background: rgba(255,255,255,0.1); width: 100%; }

.suggestion-list-mock { display: flex; flex-direction: column; }
.suggestion-item { display: flex; align-items: center; gap: 16px; padding: 16px 24px; color: #fff; transition: background 0.2s ease; }
.suggestion-item:hover { background: rgba(255,255,255,0.02); }
.entity-row .title { font-size: 1.1rem; font-weight: 800; }
.item-icon { color: #a1a1aa; display: flex; align-items: center; justify-content: center; width: 24px; }
.item-text { flex-grow: 1; display: flex; flex-direction: column; }
.item-text .title { font-size: 1rem; font-weight: 500; }
.item-text .meta { font-size: 0.85rem; color: #71717a; margin-top: 2px; }
.item-actions { color: #71717a; display: flex; align-items: center; }
.item-actions .step-trigger { cursor: pointer; transition: color 0.2s ease, transform 0.2s ease; }
.item-actions .step-trigger:hover { color: #fff; transform: scale(1.1); }
.item-arrow { color: #71717a; }
.action-pair { display: flex; gap: 24px; color: #71717a; }

@media (max-width: 768px) {
  .suggestion-item { padding: 14px 16px; }
  .expanded-title-area { padding: 16px; }
  .main-entity-title { font-size: 1.5rem; }
  .hide-mobile { display: none !important; }
}

.animate-in { animation: fadeInUp 0.4s ease forwards; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>
